/* The filter bar above a work table (APP-149 D4): menus that wrap onto as many lines as they need
   (390 px included, with no sideways scroll), a set filter as a navy chip, and the status line.
   Tokens only; every target at least 44 px. */

.bar {
  container-type: inline-size;
  display: grid;
  gap: 4px;
  min-width: 0;
  margin-bottom: 8px;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  min-width: 0;
  padding-top: 12px;
  border-top: 1px solid var(--border-card);
}

.lead {
  display: inline-flex;
  margin-right: 2px;
  color: var(--text-muted);
}

/* In a phone-width card the mark would sit on a line of its own above a wide chip. */
@container (max-width: 480px) {
  .lead {
    display: none;
  }
}

/* One filter: its menu button, and when set, the clear button joined to it as one chip. */
.filter {
  position: relative;
  display: inline-flex;
  max-width: 100%;
  min-width: 0;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  background: var(--white);
}

.menuButton {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0 6px;
  align-items: center;
  box-sizing: border-box;
  min-width: 0;
  min-height: 44px;
  padding: 0 10px 0 12px;
  border: 0;
  border-radius: inherit;
  background: none;
  color: var(--text-body);
  font-family: var(--font-body);
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}

/* Secondary ink keeps 4.5:1 on the hover tint as well as on white. */
.name {
  color: var(--text-secondary);
  font-weight: var(--fw-medium);
}

.value {
  color: var(--brand-navy);
  font-weight: var(--fw-semibold);
}

.caret {
  flex: none;
  color: var(--text-muted);
}

.menuButton[data-hovered] {
  background: var(--gray-75);
}

.menuButton[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.menuButton[aria-expanded='true'] {
  box-shadow: 0 0 0 1px var(--brand-navy);
}

/* A set filter: filled navy, its value in white, and a clear button at its end. */
.set {
  border-color: var(--brand-navy);
  background: var(--brand-navy);
}

.set .menuButton {
  border-radius: var(--radius-sm) 0 0 var(--radius-sm);
}

.set .menuButton[data-hovered] {
  background: var(--brand-navy-700);
}

.set .name {
  color: var(--brand-mist);
}

.set .value,
.set .caret {
  color: var(--white);
}

.clear {
  display: grid;
  flex: none;
  place-items: center;
  width: 44px;
  min-height: 44px;
  border: 0;
  border-left: 1px solid var(--white-a30);
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
  background: none;
  color: var(--white);
  cursor: pointer;
}

.clear[data-hovered] {
  background: var(--brand-navy-700);
}

.clear[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

/* A filter's choices: a tick for the one in force, the words, and how many rows each would show. */
.popover {
  min-width: max(240px, var(--trigger-width));
  max-width: calc(100vw - 32px);
  max-height: 340px;
  overflow-y: auto;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-md);
  background: var(--white);
  box-shadow: var(--shadow-menu);
}

.menu {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px;
  outline: none;
}

.option {
  display: flex;
  gap: 8px;
  align-items: center;
  min-height: var(--control-height);
  padding: 0 12px 0 8px;
  border-radius: var(--radius-sm);
  color: var(--text-body);
  font-size: 14px;
  cursor: pointer;
  outline: none;
}

.option[data-focused],
.option[data-hovered] {
  background: var(--brand-cyan-50);
  color: var(--brand-navy);
}

.option[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: -2px;
}

.option[data-selected] {
  color: var(--brand-navy);
  font-weight: var(--fw-semibold);
}

.tick {
  display: inline-flex;
  flex: none;
  width: 16px;
  color: var(--brand-petrol);
}

.optionLabel {
  flex: 1;
}

/* Secondary ink, not muted: it keeps 4.5:1 on the focused choice's tint too. */
.count {
  color: var(--text-secondary);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  font-weight: var(--fw-regular);
}

/* What's shown and how it's sorted, with the links that undo it. */
.line {
  display: flex;
  flex-wrap: wrap;
  gap: 0 16px;
  align-items: center;
  min-height: 44px;
  color: var(--text-muted);
  font-size: 13px;
  outline: none;
}

.status {
  margin: 0;
  font-variant-numeric: tabular-nums;
}

/* APP-254 (as APP-248's on Home): the dot between the count and the sort, drawn so it's seen but
   never part of the line's text; screen readers and a copy get the hidden full stop beside it. */
.sep::before {
  content: ' · ';
  white-space: pre;
}
